import React, { useState, useEffect, useMemo } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "../components/ui/table";
import { 
  Plus, 
  Send, 
  Trash2, 
  Eye,
  Users,
  CheckCircle,
  Clock,
  XCircle,
  Loader2,
  Star,
  CalendarIcon,
  Ban,
  Pencil,
  Copy
} from "lucide-react";
import { format } from "date-fns";
import {
  SendCampaignDialog,
  DeleteCampaignDialog,
  CancelScheduledDialog,
  ABTestResultsDialog,
  CampaignPreviewDialog,
  RecipientFilterSection,
  IndividualContactPicker,
  SelectedContactsList,
  CompactRecipientSection,
  ABTestingSection,
  ScheduleOptionsSection
} from "../components/campaigns";

export const CampaignsPage = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [gmailConnected, setGmailConnected] = useState(false);
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [campaignToSend, setCampaignToSend] = useState(null);
  const [previewCampaign, setPreviewCampaign] = useState(null);
  const [sending, setSending] = useState(false);

  // Form state
  const [campaignName, setCampaignName] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [selectedContactIds, setSelectedContactIds] = useState(new Set());
  const [senderEmail, setSenderEmail] = useState(""); // Selected sender email account

  // Filter state for adding groups
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterLocation, setFilterLocation] = useState("all");
  const [filterMinRating, setFilterMinRating] = useState("any");
  const [showIndividualPicker, setShowIndividualPicker] = useState(false);
  const [individualSearch, setIndividualSearch] = useState("");

  // Scheduling state
  const [scheduleMode, setScheduleMode] = useState("now"); // "now" or "scheduled"
  const [scheduledDate, setScheduledDate] = useState(null);
  const [scheduledTime, setScheduledTime] = useState("09:00");
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [campaignToCancel, setCampaignToCancel] = useState(null);

  // A/B Testing state
  const [abTestEnabled, setAbTestEnabled] = useState(false);
  const [abTestType, setAbTestType] = useState("subject");
  const [subjectVariants, setSubjectVariants] = useState(["", ""]);
  const [bodyVariants, setBodyVariants] = useState(["", ""]);
  const [abTestResultsDialogOpen, setAbTestResultsDialogOpen] = useState(false);
  const [abTestResults, setAbTestResults] = useState(null);
  const [abTestCampaignId, setAbTestCampaignId] = useState(null);
  const [loadingAbResults, setLoadingAbResults] = useState(false);

  // Edit campaign state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [editCampaignName, setEditCampaignName] = useState("");
  const [editTemplateId, setEditTemplateId] = useState("");
  const [editSelectedContactIds, setEditSelectedContactIds] = useState(new Set());
  const [editScheduleMode, setEditScheduleMode] = useState("now");
  const [editScheduledDate, setEditScheduledDate] = useState(null);
  const [editScheduledTime, setEditScheduledTime] = useState("09:00");
  const [editFilterCategory, setEditFilterCategory] = useState("all");
  const [editFilterLocation, setEditFilterLocation] = useState("all");
  const [editFilterMinRating, setEditFilterMinRating] = useState("any");
  const [editShowIndividualPicker, setEditShowIndividualPicker] = useState(false);
  const [editIndividualSearch, setEditIndividualSearch] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [editAbTestEnabled, setEditAbTestEnabled] = useState(false);
  const [editAbTestType, setEditAbTestType] = useState("subject");
  const [editSubjectVariants, setEditSubjectVariants] = useState(["", ""]);
  const [editBodyVariants, setEditBodyVariants] = useState(["", ""]);

  // Duplicate campaign state
  const [duplicating, setDuplicating] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [campaignsRes, templatesRes, contactsRes, categoriesRes, gmailRes] = await Promise.all([
        apiClient.get("/campaigns"),
        apiClient.get("/templates"),
        apiClient.get("/contacts?page_size=100"),
        apiClient.get("/contacts/categories"),
        apiClient.get("/oauth/gmail/status"),
      ]);
      setCampaigns(campaignsRes.data);
      setTemplates(templatesRes.data);
      const contactsList = contactsRes.data.contacts || contactsRes.data;
      // Filter out contacts without email addresses - they can't receive email campaigns
      const contactsWithEmail = contactsList.filter(c => c.email && c.email.trim() !== "");
      setContacts(contactsWithEmail);
      setCategories(categoriesRes.data);
      
      // Extract unique locations from contacts with emails only
      const uniqueLocations = [...new Set(contactsWithEmail.map(c => c.location).filter(Boolean))].sort();
      setLocations(uniqueLocations);
      
      setGmailConnected(gmailRes.data.connected);
      setEmailAccounts(gmailRes.data.accounts || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Get filtered contacts based on current filter settings
  const filteredContacts = useMemo(() => {
    return contacts.filter(c => {
      if (filterCategory !== "all" && c.category !== filterCategory) return false;
      if (filterLocation !== "all" && c.location !== filterLocation) return false;
      if (filterMinRating !== "any") {
        const minRating = parseFloat(filterMinRating);
        const contactRating = c.outscraper_data?.rating || 0;
        if (contactRating < minRating) return false;
      }
      return true;
    });
  }, [contacts, filterCategory, filterLocation, filterMinRating]);

  // Get contacts matching individual search
  const searchedContacts = useMemo(() => {
    if (!individualSearch.trim()) return [];
    const search = individualSearch.toLowerCase();
    return contacts.filter(c => 
      !selectedContactIds.has(c.id) && (
        (c.business_name && c.business_name.toLowerCase().includes(search)) ||
        (c.email && c.email.toLowerCase().includes(search)) ||
        (c.name && c.name.toLowerCase().includes(search))
      )
    ).slice(0, 10);
  }, [contacts, individualSearch, selectedContactIds]);

  // Get selected contacts details
  const selectedContacts = useMemo(() => {
    return contacts.filter(c => selectedContactIds.has(c.id));
  }, [contacts, selectedContactIds]);

  const openCreateDialog = () => {
    if (!gmailConnected) {
      toast.error("Please connect your Gmail account first in Settings");
      return;
    }
    if (templates.length === 0) {
      toast.error("Please create at least one template first");
      return;
    }
    if (contacts.length === 0) {
      toast.error("Please add contacts with valid email addresses first");
      return;
    }
    // Reset form
    setCampaignName("");
    setTemplateId("");
    setSelectedContactIds(new Set());
    setFilterCategory("all");
    setFilterLocation("all");
    setFilterMinRating("any");
    setShowIndividualPicker(false);
    setIndividualSearch("");
    setScheduleMode("now");
    setScheduledDate(null);
    setScheduledTime("09:00");
    setDialogOpen(true);
  };

  const addFilteredGroup = () => {
    const newIds = new Set(selectedContactIds);
    filteredContacts.forEach(c => newIds.add(c.id));
    setSelectedContactIds(newIds);
    toast.success(`Added ${filteredContacts.length} contacts to selection`);
  };

  const addIndividualContact = (contactId) => {
    const newIds = new Set(selectedContactIds);
    newIds.add(contactId);
    setSelectedContactIds(newIds);
    setIndividualSearch("");
  };

  const removeContact = (contactId) => {
    const newIds = new Set(selectedContactIds);
    newIds.delete(contactId);
    setSelectedContactIds(newIds);
  };

  const clearAllSelected = () => {
    setSelectedContactIds(new Set());
  };

  const handleCreate = async () => {
    if (!campaignName || !templateId || selectedContactIds.size === 0) {
      toast.error("Please fill in campaign name, select a template, and add at least one contact");
      return;
    }

    // Validate A/B testing
    if (abTestEnabled) {
      const validVariants = subjectVariants.filter(v => v.trim() !== "");
      if (validVariants.length < 2) {
        toast.error("A/B testing requires at least 2 subject line variants");
        return;
      }
    }

    // Validate scheduling if scheduled mode
    if (scheduleMode === "scheduled") {
      if (!scheduledDate) {
        toast.error("Please select a date for the scheduled campaign");
        return;
      }
      // Combine date and time
      const [hours, minutes] = scheduledTime.split(':').map(Number);
      const scheduledDateTime = new Date(scheduledDate);
      scheduledDateTime.setHours(hours, minutes, 0, 0);
      
      if (scheduledDateTime <= new Date()) {
        toast.error("Scheduled time must be in the future");
        return;
      }
    }

    try {
      let scheduled_at = null;
      if (scheduleMode === "scheduled" && scheduledDate) {
        const [hours, minutes] = scheduledTime.split(':').map(Number);
        const scheduledDateTime = new Date(scheduledDate);
        scheduledDateTime.setHours(hours, minutes, 0, 0);
        scheduled_at = scheduledDateTime.toISOString();
      }

      const campaignData = {
        name: campaignName,
        template_id: templateId,
        contact_ids: Array.from(selectedContactIds),
        scheduled_at: scheduled_at,
        sender_email: senderEmail || null, // Use selected account or default to primary
      };

      // Add A/B testing data if enabled
      if (abTestEnabled) {
        campaignData.ab_test_enabled = true;
        campaignData.ab_test_type = abTestType;
        if (abTestType === "subject") {
          campaignData.subject_variants = subjectVariants.filter(v => v.trim() !== "");
        } else {
          campaignData.body_variants = bodyVariants.filter(v => v.trim() !== "");
        }
      }

      await apiClient.post("/campaigns", campaignData);
      
      if (scheduleMode === "scheduled") {
        toast.success("Campaign scheduled successfully");
      } else {
        toast.success(abTestEnabled ? "A/B test campaign created" : "Campaign created");
      }
      setDialogOpen(false);
      // Reset form state
      setAbTestEnabled(false);
      setAbTestType("subject");
      setSubjectVariants(["", ""]);
      setBodyVariants(["", ""]);
      setSenderEmail("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create campaign");
    }
  };

  const handleCancel = async () => {
    if (!campaignToCancel) return;
    
    try {
      await apiClient.post(`/campaigns/${campaignToCancel}/cancel`);
      toast.success("Campaign cancelled and returned to draft");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel campaign");
    } finally {
      setCancelDialogOpen(false);
      setCampaignToCancel(null);
    }
  };

  const handleSend = async () => {
    if (!campaignToSend) return;
    
    setSending(true);
    try {
      const response = await apiClient.post(`/campaigns/${campaignToSend}/send`);
      toast.success(`Campaign sent! ${response.data.sent} emails delivered, ${response.data.bounced} bounced`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send campaign");
    } finally {
      setSending(false);
      setSendDialogOpen(false);
      setCampaignToSend(null);
    }
  };

  const handleDelete = async () => {
    if (!campaignToDelete) return;
    try {
      await apiClient.delete(`/campaigns/${campaignToDelete}`);
      toast.success("Campaign deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete campaign");
    }
    setDeleteDialogOpen(false);
    setCampaignToDelete(null);
  };

  const openEditDialog = (campaign) => {
    setEditingCampaign(campaign);
    setEditCampaignName(campaign.name);
    setEditTemplateId(campaign.template_id);
    setEditSelectedContactIds(new Set(campaign.contact_ids || []));
    
    // Set scheduling state
    if (campaign.scheduled_at) {
      setEditScheduleMode("scheduled");
      const scheduledDateTime = new Date(campaign.scheduled_at);
      setEditScheduledDate(scheduledDateTime);
      const hours = scheduledDateTime.getHours().toString().padStart(2, '0');
      const minutes = scheduledDateTime.getMinutes().toString().padStart(2, '0');
      setEditScheduledTime(`${hours}:${minutes}`);
    } else {
      setEditScheduleMode("now");
      setEditScheduledDate(null);
      setEditScheduledTime("09:00");
    }
    
    // Set A/B testing state
    setEditAbTestEnabled(campaign.ab_test_enabled || false);
    setEditAbTestType(campaign.ab_test_type || "subject");
    setEditSubjectVariants(campaign.subject_variants?.length > 0 ? campaign.subject_variants : ["", ""]);
    setEditBodyVariants(campaign.body_variants?.length > 0 ? campaign.body_variants : ["", ""]);
    
    // Reset filter state
    setEditFilterCategory("all");
    setEditFilterLocation("all");
    setEditFilterMinRating("any");
    setEditShowIndividualPicker(false);
    setEditIndividualSearch("");
    
    setEditDialogOpen(true);
  };

  const fetchAbTestResults = async (campaignId) => {
    setLoadingAbResults(true);
    setAbTestCampaignId(campaignId);
    try {
      const response = await apiClient.get(`/campaigns/${campaignId}/ab-test-results`);
      setAbTestResults(response.data);
      setAbTestResultsDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch A/B test results");
    } finally {
      setLoadingAbResults(false);
    }
  };

  const handleApplyWinner = async (campaignId) => {
    const response = await apiClient.post(`/campaigns/${campaignId}/apply-ab-winner`);
    // Refresh templates after applying winner
    fetchData();
    return response.data;
  };

  const handleDuplicateCampaign = async (campaignId) => {
    setDuplicating(campaignId);
    try {
      const response = await apiClient.post(`/campaigns/${campaignId}/duplicate`);
      toast.success(`Campaign duplicated as "${response.data.name}"`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to duplicate campaign");
    } finally {
      setDuplicating(null);
    }
  };

  const handleEditCampaign = async () => {
    if (!editCampaignName || !editTemplateId || editSelectedContactIds.size === 0) {
      toast.error("Please fill in campaign name, select a template, and add at least one contact");
      return;
    }

    // Validate scheduling if scheduled mode
    if (editScheduleMode === "scheduled") {
      if (!editScheduledDate) {
        toast.error("Please select a date for the scheduled campaign");
        return;
      }
      // Combine date and time
      const [hours, minutes] = editScheduledTime.split(':').map(Number);
      const scheduledDateTime = new Date(editScheduledDate);
      scheduledDateTime.setHours(hours, minutes, 0, 0);
      
      if (scheduledDateTime <= new Date()) {
        toast.error("Scheduled time must be in the future");
        return;
      }
    }

    setSavingEdit(true);
    try {
      let scheduled_at = null;
      if (editScheduleMode === "scheduled" && editScheduledDate) {
        const [hours, minutes] = editScheduledTime.split(':').map(Number);
        const scheduledDateTime = new Date(editScheduledDate);
        scheduledDateTime.setHours(hours, minutes, 0, 0);
        scheduled_at = scheduledDateTime.toISOString();
      } else if (editScheduleMode === "now" && editingCampaign.scheduled_at) {
        // Was scheduled, now changed to draft - clear the schedule
        scheduled_at = "clear";
      }

      const updateData = {
        name: editCampaignName,
        template_id: editTemplateId,
        contact_ids: Array.from(editSelectedContactIds),
        ab_test_enabled: editAbTestEnabled,
        ab_test_type: editAbTestType,
      };
      
      if (editAbTestEnabled) {
        if (editAbTestType === "subject") {
          updateData.subject_variants = editSubjectVariants.filter(v => v.trim() !== "");
        } else {
          updateData.body_variants = editBodyVariants.filter(v => v.trim() !== "");
        }
      }
      
      if (scheduled_at) {
        updateData.scheduled_at = scheduled_at;
      }

      await apiClient.put(`/campaigns/${editingCampaign.id}`, updateData);
      
      toast.success("Campaign updated successfully");
      setEditDialogOpen(false);
      setEditingCampaign(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update campaign");
    } finally {
      setSavingEdit(false);
    }
  };

  // Edit dialog filtered contacts
  const editFilteredContacts = useMemo(() => {
    return contacts.filter(contact => {
      if (editFilterCategory !== "all" && contact.category !== editFilterCategory) return false;
      if (editFilterLocation !== "all" && contact.location !== editFilterLocation) return false;
      if (editFilterMinRating !== "any") {
        const minRating = parseFloat(editFilterMinRating);
        const contactRating = parseFloat(contact.rating) || 0;
        if (contactRating < minRating) return false;
      }
      return true;
    });
  }, [contacts, editFilterCategory, editFilterLocation, editFilterMinRating]);

  const editIndividualFilteredContacts = useMemo(() => {
    if (!editIndividualSearch) return contacts.slice(0, 20);
    const search = editIndividualSearch.toLowerCase();
    return contacts.filter(c => 
      (c.email && c.email.toLowerCase().includes(search)) ||
      (c.business_name && c.business_name.toLowerCase().includes(search)) ||
      (c.name && c.name.toLowerCase().includes(search)) ||
      (c.location && c.location.toLowerCase().includes(search))
    ).slice(0, 20);
  }, [contacts, editIndividualSearch]);

  const addEditFilteredGroupToSelection = () => {
    const newSelection = new Set(editSelectedContactIds);
    editFilteredContacts.forEach(c => newSelection.add(c.id));
    setEditSelectedContactIds(newSelection);
    toast.success(`Added ${editFilteredContacts.length} contacts to selection`);
  };

  const toggleEditContactSelection = (contactId) => {
    const newSelection = new Set(editSelectedContactIds);
    if (newSelection.has(contactId)) {
      newSelection.delete(contactId);
    } else {
      newSelection.add(contactId);
    }
    setEditSelectedContactIds(newSelection);
  };

  const removeEditContact = (contactId) => {
    const newSelection = new Set(editSelectedContactIds);
    newSelection.delete(contactId);
    setEditSelectedContactIds(newSelection);
  };

  const openPreview = async (campaignId) => {
    const campaign = campaigns.find(c => c.id === campaignId);
    const template = templates.find(t => t.id === campaign?.template_id);
    if (campaign && template) {
      setPreviewCampaign({ ...campaign, template });
      setPreviewDialogOpen(true);
    }
  };

  const getStatusBadge = (status, scheduledAt) => {
    const badges = {
      draft: { icon: Clock, color: "bg-slate-100 text-slate-700", label: "Draft" },
      scheduled: { icon: CalendarIcon, color: "bg-purple-100 text-purple-700", label: "Scheduled" },
      sending: { icon: Loader2, color: "bg-blue-100 text-blue-700", label: "Sending" },
      sent: { icon: CheckCircle, color: "bg-emerald-100 text-emerald-700", label: "Sent" },
      failed: { icon: XCircle, color: "bg-red-100 text-red-700", label: "Failed" },
    };
    const badge = badges[status] || badges.draft;
    
    // For scheduled campaigns, show the scheduled time
    if (status === 'scheduled' && scheduledAt) {
      const scheduledDate = new Date(scheduledAt);
      return (
        <div className="flex flex-col">
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${badge.color}`}>
            <badge.icon className="w-3 h-3" />
            {badge.label}
          </span>
          <span className="text-xs text-slate-500 mt-0.5">
            {format(scheduledDate, "MMM d, yyyy 'at' h:mm a")}
          </span>
        </div>
      );
    }
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${badge.color}`}>
        <badge.icon className={`w-3 h-3 ${status === 'sending' ? 'animate-spin' : ''}`} />
        {badge.label}
      </span>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="campaigns-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight text-slate-900">Campaigns</h1>
          <p className="text-slate-500 mt-1">Create and send email campaigns</p>
        </div>
        <Button
          onClick={openCreateDialog}
          className="bg-orange-600 hover:bg-orange-700"
          data-testid="create-campaign-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Campaign
        </Button>
      </div>

      {!gmailConnected && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <p className="text-amber-800 font-medium">
              Please connect your Gmail account in Settings before creating campaigns.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Campaigns Table */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-200">
          <CardTitle className="font-heading flex items-center gap-2">
            <Send className="w-5 h-5" />
            {campaigns.length} Campaign{campaigns.length !== 1 ? "s" : ""}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="w-8 h-8 border-2 border-slate-200 border-t-slate-800 rounded-full animate-spin" />
            </div>
          ) : campaigns.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Send className="w-12 h-12 text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No campaigns yet</h3>
              <p className="text-slate-500 text-center max-w-md mb-4">
                Create your first email campaign to reach your contacts
              </p>
              <Button onClick={openCreateDialog} className="bg-orange-600 hover:bg-orange-700">
                <Plus className="w-4 h-4 mr-2" />
                Create Campaign
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="font-semibold text-slate-600">Name</TableHead>
                  <TableHead className="font-semibold text-slate-600">Status</TableHead>
                  <TableHead className="font-semibold text-slate-600">Recipients</TableHead>
                  <TableHead className="font-semibold text-slate-600">Sent</TableHead>
                  <TableHead className="font-semibold text-slate-600">Opens</TableHead>
                  <TableHead className="font-semibold text-slate-600">Clicks</TableHead>
                  <TableHead className="font-semibold text-slate-600">Created</TableHead>
                  <TableHead className="font-semibold text-slate-600 w-32"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.map((campaign) => (
                  <TableRow key={campaign.id} className="table-row-hover" data-testid={`campaign-row-${campaign.id}`}>
                    <TableCell className="font-medium text-slate-900">
                      <div className="flex items-center gap-2">
                        {campaign.name}
                        {campaign.ab_test_enabled && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">
                            A/B
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(campaign.status, campaign.scheduled_at)}</TableCell>
                    <TableCell className="text-slate-600">{campaign.contact_ids?.length || 0}</TableCell>
                    <TableCell className="text-slate-600">{campaign.sent_count || 0}</TableCell>
                    <TableCell className="text-slate-600">{campaign.open_count || 0}</TableCell>
                    <TableCell className="text-slate-600">{campaign.click_count || 0}</TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {new Date(campaign.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openPreview(campaign.id)}
                          className="text-slate-400 hover:text-slate-600"
                          data-testid={`preview-campaign-${campaign.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {campaign.ab_test_enabled && campaign.status === "sent" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => fetchAbTestResults(campaign.id)}
                            className="text-blue-500 hover:text-blue-600"
                            data-testid={`ab-results-${campaign.id}`}
                            title="View A/B test results"
                          >
                            <span className="text-xs font-bold">A/B</span>
                          </Button>
                        )}
                        {(campaign.status === "draft" || campaign.status === "scheduled") && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(campaign)}
                            className="text-slate-400 hover:text-blue-600"
                            data-testid={`edit-campaign-${campaign.id}`}
                            title="Edit campaign"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        )}
                        {campaign.status === "draft" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setCampaignToSend(campaign.id);
                              setSendDialogOpen(true);
                            }}
                            className="text-orange-500 hover:text-orange-600"
                            data-testid={`send-campaign-${campaign.id}`}
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                        )}
                        {campaign.status === "scheduled" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setCampaignToCancel(campaign.id);
                              setCancelDialogOpen(true);
                            }}
                            className="text-purple-500 hover:text-purple-600"
                            data-testid={`cancel-campaign-${campaign.id}`}
                            title="Cancel scheduled campaign"
                          >
                            <Ban className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDuplicateCampaign(campaign.id)}
                          disabled={duplicating === campaign.id}
                          className="text-slate-400 hover:text-green-600"
                          data-testid={`duplicate-campaign-${campaign.id}`}
                          title="Duplicate campaign"
                        >
                          {duplicating === campaign.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setCampaignToDelete(campaign.id);
                            setDeleteDialogOpen(true);
                          }}
                          className="text-slate-400 hover:text-red-600"
                          data-testid={`delete-campaign-${campaign.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Campaign Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Campaign</DialogTitle>
            <DialogDescription>
              Select contacts by filtering groups or adding individuals. Only contacts with valid email addresses are shown.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Campaign Name & Template */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="campaign-name">Campaign Name</Label>
                <Input
                  id="campaign-name"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  placeholder="e.g., January Newsletter"
                  className="mt-1.5"
                  data-testid="campaign-name-input"
                />
              </div>
              <div>
                <Label>Email Template</Label>
                <Select value={templateId} onValueChange={setTemplateId}>
                  <SelectTrigger className="mt-1.5" data-testid="template-select">
                    <SelectValue placeholder="Select a template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Sender Email Account */}
            {emailAccounts.length > 1 && (
              <div>
                <Label>Send From</Label>
                <Select value={senderEmail || "primary"} onValueChange={(val) => setSenderEmail(val === "primary" ? "" : val)}>
                  <SelectTrigger className="mt-1.5" data-testid="sender-email-select">
                    <SelectValue placeholder="Use primary account" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="primary">Use Primary Account</SelectItem>
                    {emailAccounts.map((account) => (
                      <SelectItem key={account.id} value={account.email}>
                        {account.email} {account.is_primary && "(Primary)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Select which email account to send from, or leave blank to use the primary account.
                </p>
              </div>
            )}

            {/* Add by Filter Section */}
            <RecipientFilterSection
              filterCategory={filterCategory}
              setFilterCategory={setFilterCategory}
              filterLocation={filterLocation}
              setFilterLocation={setFilterLocation}
              filterMinRating={filterMinRating}
              setFilterMinRating={setFilterMinRating}
              categories={categories}
              locations={locations}
              filteredCount={filteredContacts.length}
              onAddFiltered={addFilteredGroup}
              testIdPrefix=""
            />

            {/* Add Individual Contacts */}
            <IndividualContactPicker
              searchValue={individualSearch}
              onSearchChange={setIndividualSearch}
              searchedContacts={searchedContacts}
              onAddContact={addIndividualContact}
              testIdPrefix=""
            />

            {/* Selected Contacts */}
            <SelectedContactsList
              selectedContacts={selectedContacts}
              onRemoveContact={removeContact}
              onClearAll={clearAllSelected}
            />

            {/* A/B Testing Section */}
            <ABTestingSection
              enabled={abTestEnabled}
              setEnabled={setAbTestEnabled}
              testType={abTestType}
              setTestType={setAbTestType}
              subjectVariants={subjectVariants}
              setSubjectVariants={setSubjectVariants}
              bodyVariants={bodyVariants}
              setBodyVariants={setBodyVariants}
              testIdPrefix=""
            />

            {/* Schedule Options */}
            <ScheduleOptionsSection
              scheduleMode={scheduleMode}
              setScheduleMode={setScheduleMode}
              scheduledDate={scheduledDate}
              setScheduledDate={setScheduledDate}
              scheduledTime={scheduledTime}
              setScheduledTime={setScheduledTime}
              testIdPrefix=""
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!campaignName || !templateId || selectedContactIds.size === 0 || (scheduleMode === "scheduled" && !scheduledDate)}
              className={scheduleMode === "scheduled" ? "bg-purple-600 hover:bg-purple-700" : "bg-slate-900 hover:bg-slate-800"}
              data-testid="create-campaign-submit-btn"
            >
              {scheduleMode === "scheduled" ? (
                <>
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  Schedule Campaign ({selectedContactIds.size} recipients)
                </>
              ) : (
                `Create Campaign (${selectedContactIds.size} recipients)`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Confirmation Dialog */}
      <SendCampaignDialog
        open={sendDialogOpen}
        onOpenChange={setSendDialogOpen}
        onConfirm={handleSend}
        sending={sending}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteCampaignDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
      />

      {/* Preview Dialog */}
      <CampaignPreviewDialog
        open={previewDialogOpen}
        onOpenChange={setPreviewDialogOpen}
        campaign={previewCampaign}
        getStatusBadge={getStatusBadge}
      />

      {/* Cancel Scheduled Campaign Dialog */}
      <CancelScheduledDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        onConfirm={handleCancel}
      />

      {/* Edit Campaign Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Pencil className="w-5 h-5" />
              Edit Campaign
            </DialogTitle>
            <DialogDescription>
              Modify campaign details, recipients, and scheduling.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Campaign Name */}
            <div>
              <Label htmlFor="edit-campaign-name">Campaign Name</Label>
              <Input
                id="edit-campaign-name"
                value={editCampaignName}
                onChange={(e) => setEditCampaignName(e.target.value)}
                placeholder="e.g., Spring Newsletter"
                className="mt-1.5"
                data-testid="edit-campaign-name-input"
              />
            </div>

            {/* Template Selection */}
            <div>
              <Label>Email Template</Label>
              <Select value={editTemplateId} onValueChange={setEditTemplateId}>
                <SelectTrigger className="mt-1.5" data-testid="edit-template-select">
                  <SelectValue placeholder="Select a template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Recipients Section */}
            <CompactRecipientSection
              contacts={contacts}
              selectedContactIds={editSelectedContactIds}
              categories={categories}
              locations={locations}
              filterCategory={editFilterCategory}
              setFilterCategory={setEditFilterCategory}
              filterLocation={editFilterLocation}
              setFilterLocation={setEditFilterLocation}
              filterMinRating={editFilterMinRating}
              setFilterMinRating={setEditFilterMinRating}
              filteredCount={editFilteredContacts.length}
              onAddFilteredGroup={addEditFilteredGroupToSelection}
              showIndividualPicker={editShowIndividualPicker}
              setShowIndividualPicker={setEditShowIndividualPicker}
              individualSearch={editIndividualSearch}
              setIndividualSearch={setEditIndividualSearch}
              individualFilteredContacts={editIndividualFilteredContacts}
              onToggleContact={toggleEditContactSelection}
              onRemoveContact={removeEditContact}
              onClearAll={() => setEditSelectedContactIds(new Set())}
              testIdPrefix="edit-"
            />

            {/* A/B Testing Section */}
            <ABTestingSection
              enabled={editAbTestEnabled}
              setEnabled={setEditAbTestEnabled}
              testType={editAbTestType}
              setTestType={setEditAbTestType}
              subjectVariants={editSubjectVariants}
              setSubjectVariants={setEditSubjectVariants}
              bodyVariants={editBodyVariants}
              setBodyVariants={setEditBodyVariants}
              testIdPrefix="edit-"
            />

            {/* Schedule Options */}
            <ScheduleOptionsSection
              scheduleMode={editScheduleMode}
              setScheduleMode={setEditScheduleMode}
              scheduledDate={editScheduledDate}
              setScheduledDate={setEditScheduledDate}
              scheduledTime={editScheduledTime}
              setScheduledTime={setEditScheduledTime}
              testIdPrefix="edit-"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleEditCampaign}
              disabled={savingEdit || !editCampaignName || !editTemplateId || editSelectedContactIds.size === 0 || (editScheduleMode === "scheduled" && !editScheduledDate)}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="update-campaign-btn"
            >
              {savingEdit ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </span>
              ) : (
                `Update Campaign (${editSelectedContactIds.size} recipients)`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* A/B Test Results Dialog */}
      <ABTestResultsDialog
        open={abTestResultsDialogOpen}
        onOpenChange={setAbTestResultsDialogOpen}
        results={abTestResults}
        loading={loadingAbResults}
        campaignId={abTestCampaignId}
        onApplyWinner={handleApplyWinner}
      />
    </div>
  );
};
